import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Space, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import GcPagina from '../Layout/GcPagina';
import {
  confirmarVerificacion,
  descargarDocumento,
  leerDisponibilidadLectura,
  leerLecturaCaso,
  mensajeError,
  solicitarLectura,
} from '../../api/feriasApi';
import type { LecturaCaso, Relacion, ValorCampo } from '../../api/feriasApi';
import { FctAviso } from '../../fct/react';
import { CampoEditor } from './CampoEditor';
import { GRUPOS, mismoValor } from './campos';
import { motivoEnPantalla } from './motivos';
import { puntoDePartida } from './puntoDePartida';
import type { PuntoDePartida } from './puntoDePartida';
import { fechaHoraMadrid } from './textos';
import './Ferias.css';

/** La bandeja, a la que vuelve la pantalla. */
export const RUTA_BANDEJA = '/ferias';

/**
 * Los valores sin las filas de relación añadidas y dejadas vacías: sin persona ni tipo no dicen nada, y no
 * se guardan (QA en la PR #95).
 */
export function sinRelacionesVacias(valores: Record<string, ValorCampo>): Record<string, ValorCampo> {
  const relacionadas = valores.relacionadas;
  if (!Array.isArray(relacionadas)) {
    return valores;
  }
  return {
    ...valores,
    relacionadas: (relacionadas as Relacion[]).filter((r) => r.persona.trim() !== '' || r.tipo.trim() !== ''),
  };
}

// Lo que dice la pantalla de la lectura del caso, encima de los campos. Un caso sin lectura es un
// estado vacío, no un error: se completa a mano mirando el PDF (en STG, mientras la lectura esté apagada).
function AvisoDeLectura({ caso, lecturaActiva }: { caso: LecturaCaso; lecturaActiva: boolean }) {
  const { progreso, lectura, intentos, verificacion } = caso;
  if (progreso === 'fallida') {
    return (
      <FctAviso tipo="error" titulo="La lectura automática ha fallado">
        {intentos.fallidos} {intentos.fallidos === 1 ? 'intento fallido' : 'intentos fallidos'}
        {intentos.ultimoError ? `: ${intentos.ultimoError}` : '.'} Complete los campos a mano mirando el PDF, o pida una nueva lectura.
      </FctAviso>
    );
  }
  if (progreso === 'esperando' && !lectura) {
    return lecturaActiva ? (
      <FctAviso tipo="info" titulo="Esta ficha aún no tiene lectura">
        Puede esperar a la lectura automática o completar los campos a mano mirando el PDF.
      </FctAviso>
    ) : (
      <FctAviso tipo="info" titulo="Esta ficha no tiene lectura">
        La lectura automática está desactivada. Complete los campos a mano mirando el PDF y confirme.
      </FctAviso>
    );
  }
  if (progreso === 'esperando') {
    return (
      <FctAviso tipo="progreso" titulo="Hay una nueva lectura pedida">
        Mientras llega, se muestran los últimos valores {verificacion ? 'verificados' : 'leídos'}.
      </FctAviso>
    );
  }
  if (progreso === 'verificadoNuevaLectura') {
    return (
      <FctAviso tipo="aviso" titulo="Nueva lectura de una ficha ya verificada">
        Junto a cada campo se muestra el valor verificado antes. Al confirmar se guarda una verificación nueva; la anterior se
        conserva.
      </FctAviso>
    );
  }
  if (progreso === 'verificado' && verificacion) {
    return (
      <FctAviso tipo="exito" titulo={`Verificada el ${fechaHoraMadrid(verificacion.verificadaEn)}`}>
        Si confirma de nuevo se guarda otra verificación; la anterior se conserva.
      </FctAviso>
    );
  }
  return (
    <FctAviso tipo="info" titulo="Revise todos los campos">
      Un campo marcado «Leído» también puede estar mal leído; los «Dudoso» llevan la nota del modelo.
    </FctAviso>
  );
}

/**
 * La pantalla de verificación (diseño M3 §4 de fct-ferias, CA02, M3-04): el PDF del caso junto a todos
 * los campos del contrato, agrupados como la fuente §7, cada uno editable con su estado de lectura; se
 * confirman todos a la vez. Con correcciones sin guardar, la propia pantalla pregunta antes de volver a
 * la bandeja y el navegador antes de cerrar la pestaña (M3-05).
 */
const VerificacionCaso: React.FC = () => {
  const idCaso = Number(useParams().idCaso);
  const navegar = useNavigate();
  const [caso, setCaso] = useState<LecturaCaso | null>(null);
  const [punto, setPunto] = useState<PuntoDePartida | null>(null);
  const [valores, setValores] = useState<Record<string, ValorCampo>>({});
  const [pdf, setPdf] = useState<string | null>(null);
  const [lecturaActiva, setLecturaActiva] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // La lectura del caso y los valores de los que parten los campos; y si las fichas se leen solas.
  useEffect(() => {
    leerLecturaCaso(idCaso)
      .then((leido) => {
        const inicio = puntoDePartida(leido);
        setCaso(leido);
        setPunto(inicio);
        setValores(inicio.valores);
      })
      .catch((e: unknown) => setError(motivoEnPantalla(mensajeError(e))));
    leerDisponibilidadLectura()
      .then((d) => setLecturaActiva(d.habilitada))
      .catch(() => setLecturaActiva(true));
  }, [idCaso]);

  // El PDF va con el token: se pide como blob y se muestra por su dirección local, que se libera al salir.
  useEffect(() => {
    let direccion: string | null = null;
    descargarDocumento(idCaso)
      .then((blob) => {
        direccion = URL.createObjectURL(blob);
        setPdf(direccion);
      })
      .catch((e: unknown) => setError(motivoEnPantalla(mensajeError(e))));
    return () => {
      if (direccion) {
        URL.revokeObjectURL(direccion);
      }
    };
  }, [idCaso]);

  const sinGuardar = punto !== null && !mismoValor(valores, punto.valores);

  // El navegador pregunta antes de cerrar la pestaña con correcciones sin guardar.
  useEffect(() => {
    if (!sinGuardar) {
      return undefined;
    }
    const alCerrar = (evento: BeforeUnloadEvent) => {
      evento.preventDefault();
      evento.returnValue = '';
    };
    window.addEventListener('beforeunload', alCerrar);
    return () => window.removeEventListener('beforeunload', alCerrar);
  }, [sinGuardar]);

  const cambiar = useCallback((clave: string, valor: ValorCampo) => setValores((actuales) => ({ ...actuales, [clave]: valor })), []);

  const volver = () => {
    if (!sinGuardar) {
      navegar(RUTA_BANDEJA);
      return;
    }
    Modal.confirm({
      title: 'Hay correcciones sin guardar',
      content: 'Si vuelve a la bandeja se pierden. Para conservarlas, confirme la lectura.',
      okText: 'Volver sin guardar',
      cancelText: 'Seguir verificando',
      // Destructivo, no primario: la pantalla conserva un solo primario (manual, regla 2).
      okType: 'default',
      okButtonProps: { danger: true },
      onOk: () => navegar(RUTA_BANDEJA),
    });
  };

  const confirmar = async () => {
    if (!punto) {
      return;
    }
    setGuardando(true);
    try {
      await confirmarVerificacion(idCaso, punto.idLectura, sinRelacionesVacias(valores));
      // Después viene el cliente del caso (FERIAS-T09).
      navegar(`/ferias/casos/${idCaso}/cliente`);
    } catch (e) {
      setError(motivoEnPantalla(mensajeError(e)));
      setGuardando(false);
    }
  };

  // Una lectura nueva (P3): las correcciones en curso se quedan en la pantalla.
  const pedirLectura = async () => {
    try {
      await solicitarLectura(idCaso);
      setCaso(await leerLecturaCaso(idCaso));
      setAviso('Nueva lectura pedida. La bandeja indicará cuándo está lista para verificar.');
    } catch (e) {
      setError(motivoEnPantalla(mensajeError(e)));
    }
  };

  return (
    <GcPagina
      seccion={['Ferias', 'Bandeja']}
      titulo={`Verificar ficha #${idCaso}`}
      contexto={sinGuardar ? 'Hay correcciones sin guardar' : 'Sin cambios'}
      acciones={
        <Space>
          <Button onClick={volver}>Volver a la bandeja</Button>
          <Button
            onClick={() => void pedirLectura()}
            disabled={!caso || !lecturaActiva}
            title={lecturaActiva ? undefined : 'La lectura automática está desactivada'}
          >
            Pedir nueva lectura
          </Button>
          <Button type="primary" onClick={() => void confirmar()} loading={guardando} disabled={!punto}>
            Confirmar lectura
          </Button>
        </Space>
      }
    >
      {error && (
        <FctAviso tipo="error" titulo="No se ha podido completar la operación">
          {error}
        </FctAviso>
      )}
      {aviso && <FctAviso tipo="exito" titulo={aviso} />}

      <div className="ferias-verificacion">
        {pdf ? (
          <iframe className="ferias-verificacion__pdf" title={`PDF de la ficha #${idCaso}`} src={pdf} />
        ) : (
          <div className="ferias-verificacion__pdf">{!error && <Spin aria-label="Cargando el PDF" />}</div>
        )}

        <div className="ferias-verificacion__campos">
          {!caso || !punto ? (
            !error && <Spin aria-label="Cargando la lectura" />
          ) : (
            <>
              <AvisoDeLectura caso={caso} lecturaActiva={lecturaActiva} />
              {GRUPOS.map((grupo) => (
                <section key={grupo.titulo} className="ferias-grupo" aria-label={grupo.titulo}>
                  <h2 className="ferias-grupo__titulo">{grupo.titulo}</h2>
                  {grupo.nota && <FctAviso tipo="info" titulo={grupo.nota} />}
                  {grupo.campos.map((campo) => (
                    <CampoEditor
                      key={campo.clave}
                      campo={campo}
                      valor={valores[campo.clave]}
                      onCambio={(valor) => cambiar(campo.clave, valor)}
                      lectura={punto.lectura?.[campo.clave]}
                      anterior={punto.anterior ? punto.anterior[campo.clave] : undefined}
                    />
                  ))}
                </section>
              ))}
            </>
          )}
        </div>
      </div>
    </GcPagina>
  );
};

export default VerificacionCaso;
